type DataItem = {
  [key: string]: any
}

type UniqueValuesResult = {
  [key: string]: any[]
}

/**
 * Extracts unique values from an array of objects based on specified keys.
 * Supports arrays of strings and objects with an 'id' key.
 * @param data - The array of objects from which to extract unique values.
 * @param keys - An array of strings representing the keys in each object containing the arrays of values to extract.
 * @returns An object containing unique values extracted from each specified key across all objects.
 */
export const extractUniqueValues = (
  data: DataItem[],
  keys: string[],
): UniqueValuesResult => {
  const uniqueValues: Record<string, Set<any>> = {}

  keys.forEach((key) => {
    uniqueValues[key] = new Set()

    data.forEach((item) => {
      const value = item[key]

      if (Array.isArray(value)) {
        value.forEach((subItem) => {
          if (typeof subItem === "string") {
            uniqueValues[key].add(subItem)
          } else if (
            typeof subItem === "object" &&
            subItem !== null &&
            "id" in subItem
          ) {
            uniqueValues[key].add(JSON.stringify(subItem))
          }
        })
      }
    })
  })

  const result: UniqueValuesResult = {}
  Object.entries(uniqueValues).forEach(([key, set]) => {
    result[key] = Array.from(set).map((value) => {
      if (typeof value === "string") {
        try {
          return JSON.parse(value)
        } catch {
          return value
        }
      }
      return value
    })
  })

  return result
}
